<template>
  <jr-button class="send-btn" type="primary" @click="confirm"> {{ buttonLabel }} </jr-button>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'
import { MESSAGE_TYPE_CONFIRM, addMessageQueue } from '@/plugins/messageQueue'
import { ElForm } from 'element-plus'
import { CandidateSendRequest } from '@/requests/schedule'
import JrButton from '@/components/jr-parts/JrButton.vue'
import MessageAttacheFiles from '@/components/inputs/MessageAttacheFiles.vue'
import Message from '@/services/message'
import { ScheduleSendForm } from '@/types/forms'
import { ScheduleCandidateDateSendModeEnum, ScheduleCandidateDatePost } from '@/api'
import { isAfterToday } from '@/services/date'
import { TEXT_FIELD_REQUEST_MAX_LEN } from '@/constants/backendConstants'
import { MESSAGE_BODY } from '@/constants/rules'

/**
 * 日程調整送信ボタン
 */
export default defineComponent({
  name: 'ScheduleSend',
  emits: ['update:isSent'],
  components: {
    JrButton,
  },
  props: {
    // メッセージ内容
    message: {
      type: Object as PropType<ScheduleSendForm>,
      required: true,
    },
    formRef: {
      type: Object as PropType<InstanceType<typeof ElForm>>,
      required: false,
      default: null,
    },
    // 添付ファイルコンポーネントref
    attacheFileRef: {
      type: Object as PropType<InstanceType<typeof MessageAttacheFiles>>,
      required: false,
      default: null,
    },
    // モード
    mode: {
      type: String as PropType<ScheduleCandidateDateSendModeEnum>,
      required: true,
    },
  },
  setup(props, context) {
    const buttonLabel = computed(() => {
      if (
        props.mode === ScheduleCandidateDateSendModeEnum.Send ||
        props.mode === ScheduleCandidateDateSendModeEnum.SaveSend
      ) {
        return '送信'
      } else if (props.mode === ScheduleCandidateDateSendModeEnum.SaveOnly) {
        return '修正の保存のみ'
      } else if (props.mode === ScheduleCandidateDateSendModeEnum.TmpSaveOnly) {
        return '一時保存'
      } else if (props.mode === ScheduleCandidateDateSendModeEnum.SendAddUser) {
        return '追加した人にのみ送信'
      } else if (props.mode === ScheduleCandidateDateSendModeEnum.SendAllUser) {
        return '全員に送信'
      }
      return ''
    })

    /**
     * メッセージ送信
     */
    const send = async (candidates: ScheduleCandidateDatePost[] | null | undefined) => {
      // チェックOKで添付ファイルがあれば送信
      if (props.attacheFileRef != null) {
        await props.attacheFileRef.allUpload()
      }
      // 送信区分を変更
      const message = { ...props.message }
      message.mode = props.mode as ScheduleCandidateDateSendModeEnum
      message.schedule_candidate_date = candidates

      //回答期限が空文字でAPIで渡るとエラーになるためnullに変更
      if (message.answer_expiration === '') {
        message.answer_expiration = null
      }

      // メッセージ送信
      const request = new CandidateSendRequest(message)
      await request.exec()
      context.emit('update:isSent', request.success)
    }
    /**
     * 送信確認
     */
    const confirm = () => {
      if (!props.formRef) {
        // DOM未生成時は何もしない
        return
      }
      // バリデーションチェック
      props.formRef.validate((valid, fieldErrorObject) => {
        if (props.mode === ScheduleCandidateDateSendModeEnum.TmpSaveOnly) {
          //一時保存時は、宛先、回答期限、メッセージは空を許容するため、備考のみバリデーションチェック
          for (let field in fieldErrorObject) {
            if (field === 'remark' || field === 'error_candidate_dates') {
              //宛先又はメッセージが空の場合バリデーションメッセージをクリア
              props.formRef.clearValidate('message_address')
              props.formRef.clearValidate('body')
              props.formRef.clearValidate('answer_expiration')
              return false
            } else if (
              fieldErrorObject[field][0].message ===
              Message.bind(Message.codes.V_MAX_LENGTH, {
                attribute: MESSAGE_BODY,
                max: TEXT_FIELD_REQUEST_MAX_LEN.toString(),
              })
            ) {
              props.formRef.clearValidate('message_address')
              props.formRef.clearValidate('answer_expiration')
              props.formRef.clearValidate('schedule_candidate_date')
              return false
            }
          }
          props.formRef.clearValidate()
        } else if (!valid) {
          return false
        }

        if (
          props.mode === ScheduleCandidateDateSendModeEnum.Send ||
          props.mode === ScheduleCandidateDateSendModeEnum.SaveSend ||
          props.mode === ScheduleCandidateDateSendModeEnum.SendAddUser ||
          props.mode === ScheduleCandidateDateSendModeEnum.SendAllUser
        ) {
          //送信時は今日を含む過去日付の日程候補は削除する。
          let candidates: ScheduleCandidateDatePost[] = []
          if (props.message.schedule_candidate_date != null) {
            candidates = props.message.schedule_candidate_date.filter((candidate) => {
              return isAfterToday(new Date(candidate.date))
            })
          }
          addMessageQueue(MESSAGE_TYPE_CONFIRM, Message.codes.CONFIRM_SEND, async () => await send(candidates))
        } else {
          addMessageQueue(
            MESSAGE_TYPE_CONFIRM,
            Message.codes.CONFIRM_SAVE,
            async () => await send(props.message.schedule_candidate_date)
          )
        }

        return true
      })
    }

    return {
      buttonLabel,
      confirm,
    }
  },
})
</script>
